/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import React from 'react';
import { withRouter } from 'react-router';
import { object, string, bool } from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet';
import { compose } from 'recompose';

// Redux
import { trackData } from '../../../../helpers/tracking/common/commonTrackingHelpers';
import { getBrand } from '../../../../../state/ducks/App/ducks/Brand/Brand-Selectors';
import { getFeatureFlag } from '../../../../../state/ducks/App/ducks/Config/Config-Selectors';
import { sanitizeContent } from '../../../../../lib/sanitize';

const GraphqlDesktopSeoHeadTags = ({
    seoData, brand, location: { pathname }, noscript = '', ffIsSmartAppBannerEnabled,
}) => {
    if (seoData) {
        const {
            page_title = '', meta_description = '', keywords = '', og = [], seo_searchable = true, canonicalUrl = '', canonical_url = '',
        } = seoData || {};
        // Persist data layer
        trackData({
            seoData,
            action: 'track_seo_data',
        });

        const determinePath = (pathName) => (pathName === '/' ? '' : pathName);

        // IF canonicalUrl exsists on PDP return it
        // ELSE return concatenated string
        const setCanonicalUrl = () => {
            if (canonicalUrl || canonical_url) {
                return canonicalUrl || canonical_url;
            }
            return `https://www.${brand.domain}.com${determinePath(pathname)}`;
        };

        const canonicalUrlIsFromDifferentBrand = () => {
            if (canonicalUrl && !canonicalUrl?.includes(brand['domain-name'])) {
                return true;
            }
            return false;
        };

        // Copy so we can modify
        const newOg = og && og.slice ? og.slice() : [];
        // Primary use case is CONTENT/DEPARTMENT pages - look into adding the data at page level
        // add og:url to og array if it does not exist
        if (!newOg?.some((graph) => (graph.property === 'og:url') && (graph.content?.length > 0))) {
            newOg.push({
                property: 'og:url',
                content: setCanonicalUrl(),
            });
        }

        return (
            <Helmet>
                {page_title && (<title>{sanitizeContent(page_title)}</title>)}
                {/* some pages we dont want searchable(toggle in CMS), ONLY display meta if not true */}
                {!seo_searchable && <meta name="robots" content="noindex,nofollow" />}
                {canonicalUrlIsFromDifferentBrand() && <meta name="robots" content="noindex,follow" />}
                {keywords && <meta name="keywords" content={keywords} />}
                {meta_description && <meta name="description" content={meta_description} />}
                {brand && pathname && <link rel="canonical" href={setCanonicalUrl()} />}
                {/* TODO: temporary until all brands desktop/mobile are on 4.2 */}
                {newOg && newOg.length > 0 && Array.isArray(newOg) && newOg.map((d) => (<meta key={d.property} property={d.property} content={d.content} />))}
                {/* IF brand is 18F, we add App Smart Banner */}
                {ffIsSmartAppBannerEnabled && brand && brand['domain-name'] && brand['smart-app-banner-app-id'] && <meta name="apple-itunes-app" content={`app-id=${brand['smart-app-banner-app-id']}`} />}
                {ffIsSmartAppBannerEnabled && brand && brand['domain-name'] && brand['smart-app-banner-android-app-id'] && <meta name="google-play-app" content={`app-id=${brand['smart-app-banner-android-app-id']}`} />}
                <noscript>
                    {`1800flowers ${noscript}`}
                </noscript>
            </Helmet>
        );
    }
    return null;
};

GraphqlDesktopSeoHeadTags.propTypes = {
    seoData: object.isRequired,
    brand: object.isRequired,
    location: object.isRequired,
    noscript: string,
    ffIsSmartAppBannerEnabled: bool.isRequired,
};
GraphqlDesktopSeoHeadTags.defaultProps = {
    noscript: '',
};
const mapStateToProps = (state) => ({
    brand: getBrand(state),
    ffIsSmartAppBannerEnabled: getFeatureFlag('is-smart-app-banner-enabled')(state),
});

const enhance = compose(
    withRouter,
    connect(mapStateToProps),
);

export default enhance(GraphqlDesktopSeoHeadTags);
